import { useState } from "react";
import { Link } from "react-router-dom";
import { Stack, Group, Text, Badge, Timeline, Paper, ThemeIcon, Center, Collapse, Loader, Indicator, ActionIcon, Tooltip } from "@mantine/core";
import { IconClipboardText, IconChevronDown, IconChevronUp, IconClock, IconClipboardList, IconClipboardCheck, IconClipboardPlus, IconExternalLink } from "@tabler/icons-react";
import moment from "moment-timezone";
import { PastExecutionDto } from "../../models/workorderschedule.models";
import classes from './PastExecutions.module.css';
import { getWorkOrderStatusColor, getWorkOrderStatusDescription } from "../../functions/workorder.functions";

interface PastExecutionsProps {
	pastExecutions: PastExecutionDto[] | undefined;
	isLoading?: boolean;
}

export const PastExecutions = ({ pastExecutions, isLoading }: PastExecutionsProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleOpenWorkOrder = (e: React.MouseEvent<HTMLButtonElement>, workOrderId: string) => {
		e.preventDefault();
		e.stopPropagation();
		window.open(`/workorders/${workOrderId}`, "_blank");
	}

	if (isLoading) {
		return (
			<Center py="md">
				<Loader size="sm" />
			</Center>
		);
	}

	if (!pastExecutions || pastExecutions.length === 0) {
		return (
			<Center py="md">
				<Text c="dimmed">No past executions</Text>
			</Center>
		);
	}

	return (
		<Stack gap="md" my="xl">
			<Group 
				justify="space-between"
				onClick={() => setIsOpen(!isOpen)}
				style={{ cursor: "pointer" }}
			>
				<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Past Executions ({pastExecutions.length})</Text>
				<Group gap="xs">
					{/* <Badge color="gray" variant="light">
						{pastExecutions.length} {pastExecutions.length === 1 ? 'execution' : 'executions'}
					</Badge> */}
					{isOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
				</Group>
			</Group>

			<Collapse in={isOpen}>
				<Timeline  bulletSize={24} lineWidth={2}>
					{pastExecutions.map((execution, index) => (
						<Timeline.Item
							key={execution.WorkOrderId}
							bullet={
								<ThemeIcon size="sm" variant="filled" radius="xl">
									<IconClock size={20} />
								</ThemeIcon>
							}
							title={
								<Text fw={500} size="sm">
									{moment(execution.Created).format("dddd, MMMM DD, YYYY")}
								</Text>
							}
						>
							<Paper component={Link} to={`/workorders/${execution.WorkOrderId}`} p="sm" radius="md" className={classes['executionPaper']}>
								<Group justify="space-between" align="center" gap="xs" wrap="nowrap">
									<Group gap="xs" wrap="nowrap">
										<ThemeIcon radius="xl"
											// color={isRequest ? "teal" : ""}
											size="xl"
											style={{ border: `1px solid var(--mantine-color-default-border)` }}
											variant="light"
										>
											
											<IconClipboardList size={24} />
										</ThemeIcon>
										<Group>
											<Stack gap="3">
												<Group gap="xs">
													<Text size="md">
														{execution.Title}
													</Text>
													<Badge variant="light" color={getWorkOrderStatusColor(execution.Status)} size="md" radius="sm">
														{getWorkOrderStatusDescription(execution.Status)}
													</Badge>
												</Group>
												<Text size="xs" c="dimmed">
													Last updated {moment.utc(execution.Updated).local().format("LLL")}
												</Text>
											</Stack>
										</Group>
									</Group>
									<Tooltip label="Open in new tab" withArrow>
										<ActionIcon variant="light" radius="md" onClick={(e) => handleOpenWorkOrder(e, execution.WorkOrderId)}>
											<IconExternalLink size={16} />
										</ActionIcon>
									</Tooltip>
								</Group>
							</Paper>
						</Timeline.Item>
					))}
				</Timeline>
			</Collapse>
		</Stack>
	);
};
